/**
 * The `.bru` block language, read the way Bruno itself reads it
 * (`usebruno/bruno` packages/bruno-lang/v2 — grammar and `jsonToBru.js`).
 *
 * A file is a sequence of top-level blocks. Each block header starts at
 * column 0 (`headers {`, `body:json {`, `vars:secret [`), its lines are
 * indented by two spaces, and it ENDS at the first line that is exactly `}`
 * (or `]`) at column 0. Ending on that line rather than on a balanced brace
 * count is what lets a script or JSON body contain an unbalanced `{` inside a
 * string — the old reader counted braces and could swallow the next block.
 *
 * Dictionary lines:
 *   key: value                 plain
 *   ~key: value                disabled
 *   "key: with {odd} chars": v quoted key (`\"` escapes), as Bruno writes it
 *   key: '''                   multi-line value, until a line that starts
 *     line one                 with `'''` (which may carry a suffix such as
 *     line two                 ` @contentType(x)` in a multipart row)
 *   '''
 *   @number / @description('…') annotation lines before a key (skipped)
 *   key: [ … ]                 a nested list (meta `tags`, settings
 *                              `omitHeaders`) — consumed and ignored
 */

const BLOCK_HEADER = /^([A-Za-z][\w:.-]*)\s*([{[])\s*$/;
const ANNOTATION_LINE = /^@[A-Za-z_][\w-]*(\(.*\))?\s*$/;
const ANNOTATION_BLOCK_START = /^@[A-Za-z_][\w-]*\('''\s*$/;

/** Remove Bruno's two-space block indentation (Bruno's own `outdentString(2)`). */
export function outdent(text, spaces = 2) {
  const re = new RegExp(`^ {${spaces}}`);
  return String(text || '')
    .split(/\r\n|\r|\n/)
    .map((line) => line.replace(re, ''))
    .join('\n');
}

/**
 * Split a `.bru` file into its top-level blocks, in file order.
 * @returns {Array<{ name: string, kind: 'dict'|'list', raw: string }>} `raw` is outdented.
 */
export function parseBruBlocks(text) {
  const lines = String(text || '').split(/\r\n|\r|\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const header = lines[i].match(BLOCK_HEADER);
    if (!header) {
      i += 1;
      continue;
    }
    const name = header[1].toLowerCase();
    const closer = header[2] === '[' ? ']' : '}';
    const body = [];
    i += 1;
    // Everything up to the closing line at column 0 belongs to the block.
    while (i < lines.length && lines[i].replace(/\s+$/, '') !== closer) {
      body.push(lines[i]);
      i += 1;
    }
    i += 1; // skip the closing line
    blocks.push({ name, kind: closer === ']' ? 'list' : 'dict', raw: outdent(body.join('\n')) });
  }
  return blocks;
}

/** First block of each name → its raw text; later duplicates are counted, not kept. */
export function indexBlocks(blocks) {
  const byName = {};
  const counts = {};
  for (const b of blocks) {
    counts[b.name] = (counts[b.name] || 0) + 1;
    if (!(b.name in byName)) byName[b.name] = b.raw;
  }
  return { byName, counts };
}

/** A text block (body, script, tests, docs) — outdented already; trim blank edges. */
export function textBlock(raw) {
  if (raw == null) return '';
  return String(raw).replace(/^\s*\n/, '').replace(/\s+$/, '');
}

function readQuotedKey(line) {
  // line starts with '"'
  let out = '';
  let j = 1;
  while (j < line.length) {
    const ch = line[j];
    if (ch === '\\' && j + 1 < line.length) {
      out += line[j + 1];
      j += 2;
      continue;
    }
    if (ch === '"') return { key: out, rest: line.slice(j + 1) };
    out += ch;
    j += 1;
  }
  return null;
}

/**
 * Parse a dictionary block into rows, keeping order and duplicates (a
 * form-urlencoded body may repeat a key on purpose).
 * @returns {Array<{ key: string, value: string, enabled: boolean }>}
 */
export function parseBruPairs(raw) {
  const rows = [];
  if (!raw) return rows;
  const lines = String(raw).split('\n');
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    i += 1;
    if (!trimmed) continue;

    // Annotation lines (`@number`, `@description('''…''')`) describe the NEXT
    // key; nothing Apiboo stores uses them, so they are skipped — including
    // the multi-line form, whose body would otherwise read as keys.
    if (ANNOTATION_BLOCK_START.test(trimmed)) {
      while (i < lines.length && !/^'''\)\s*$/.test(lines[i].trim())) i += 1;
      i += 1;
      continue;
    }
    if (ANNOTATION_LINE.test(trimmed)) continue;

    let line = trimmed;
    const disabled = line.startsWith('~');
    if (disabled) line = line.slice(1).trimStart();

    let key;
    let rest;
    if (line.startsWith('"')) {
      const q = readQuotedKey(line);
      if (!q) continue;
      key = q.key;
      rest = q.rest.trimStart();
      if (!rest.startsWith(':')) continue;
      rest = rest.slice(1);
    } else {
      const colon = line.indexOf(':');
      if (colon === -1) continue;
      key = line.slice(0, colon).trim();
      rest = line.slice(colon + 1);
    }
    let value = rest.trim();

    if (value === "'''") {
      // Multi-line value: its lines are indented one more level.
      const body = [];
      let suffix = '';
      while (i < lines.length) {
        const t = lines[i].trim();
        if (t.startsWith("'''")) {
          suffix = t.slice(3).trim();
          i += 1;
          break;
        }
        body.push(lines[i]);
        i += 1;
      }
      value = outdent(body.join('\n')).replace(/^\n+|\n+$/g, '');
      if (suffix) value = `${value} ${suffix}`;
    } else if (value === '[') {
      // A nested list value (meta `tags`, settings `omitHeaders`): not stored.
      while (i < lines.length && lines[i].trim() !== ']') i += 1;
      i += 1;
      continue;
    }

    if (key || value) rows.push({ key, value, enabled: !disabled });
  }
  return rows;
}

/** Rows → `{ key: value }`, first occurrence wins (meta, method block, auth). */
export function pairsToObject(rows) {
  const obj = {};
  for (const r of rows) {
    if (r.enabled && !(r.key in obj)) obj[r.key] = r.value;
  }
  return obj;
}

/** A list block (`vars:secret [a, b, ~c]`) → names; `~` marks disabled. */
export function parseBruList(raw) {
  return String(raw || '')
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.startsWith('~') ? { name: s.slice(1).trim(), enabled: false } : { name: s, enabled: true }));
}

/**
 * Strip a trailing `@contentType(x)` from a multipart / file row value.
 * @returns {{ value: string, contentType: string }}
 */
export function splitContentType(value) {
  const m = String(value || '').match(/^([\s\S]*?)\s*@contentType\(([^)]*)\)\s*$/);
  if (!m) return { value: String(value || ''), contentType: '' };
  return { value: m[1], contentType: m[2] };
}

/** `@file(a|b)` → ['a', 'b']; anything else → null. */
export function parseFileRef(value) {
  const m = String(value || '').trim().match(/^@file\(([\s\S]*)\)$/);
  if (!m) return null;
  return m[1].split('|').map((s) => s.trim()).filter(Boolean);
}
